import { Schema, MapSchema, ArraySchema, type } from '@colyseus/schema';
import { CellValue } from './constants';
import { initialBoard } from './solution';
import { PuzzleDefinition } from './types';
import { DEFAULT_ROOM_SETTINGS, type AutoCrossLevel, type RoomSettings } from './room-settings';

/** One part's board while a room plays a multi-part puzzle. */
export class PartState extends Schema {
  @type("uint32") rows!: number
  @type("uint32") columns!: number
  @type(["int8"]) board = new ArraySchema<number>()
  @type("boolean") solved = false
}

export class Player extends Schema {
  @type("string") color!: string;
  @type("string") name!: string;
  /** Who they are in the room: the same each time they join it from their device or account. Moves, undo, the host, and cursors use it. */
  @type("string") id!: string;
  @type("boolean") disconnected!: boolean;
  /** Whether this player has a move of their own to undo, or one undone to redo. */
  @type("boolean") canUndo: boolean = false;
  @type("boolean") canRedo: boolean = false;
  /** The part of a multi-part puzzle they're in (its index; -1: the whole picture, or a puzzle without parts). */
  @type("int8") part: number = -1;
}

/** The host's settings for everyone in the room (see `RoomSettings`). */
export class RoomSettingsState extends Schema {
  @type("string") autoCross: AutoCrossLevel = DEFAULT_ROOM_SETTINGS.autoCross;
  @type("boolean") hints: boolean = DEFAULT_ROOM_SETTINGS.hints;
  @type("boolean") checks: boolean = DEFAULT_ROOM_SETTINGS.checks;

  toSettings(): RoomSettings {
    return { autoCross: this.autoCross, hints: this.hints, checks: this.checks };
  }
}

export class GameStateSchema extends Schema {
  @type({ map: Player }) players = new MapSchema<Player>();
  /** The host's player id. */
  @type("string") hostId!: string;
  @type(RoomSettingsState) settings = new RoomSettingsState();
  
  // In-game state
  @type(["int8"]) board: ArraySchema<number>;
  // puzzleId is present when in-game
  @type("string") puzzleId!: string | null
  @type("uint32") columns!: number
  @type("uint32") rows!: number
  /**
   * A multi-part puzzle's parts, in order, each with its own board (empty for
   * a puzzle without parts). While they're played, `board` is empty and
   * `rows`/`columns` are the whole picture's.
   */
  @type([PartState]) parts = new ArraySchema<PartState>()

  constructor() {
    super();
    this.board = new ArraySchema<number>();
  }

  /** Starts a puzzle on a fresh board, or, with `parts` (a multi-part puzzle's, in order), a fresh board for each part. */
  public startPuzzle(puzzleDefinition: PuzzleDefinition, parts?: readonly PuzzleDefinition[]) {
    this.puzzleId = puzzleDefinition.id;
    this.rows = puzzleDefinition.rows;
    this.columns = puzzleDefinition.columns;
    this.board = parts ? new ArraySchema<number>() : boardOf(puzzleDefinition);
    this.parts = new ArraySchema<PartState>(...(parts ?? []).map((part) => {
      // Set after construction: the class's field defaults would replace constructor values.
      const state = new PartState();
      state.rows = part.rows;
      state.columns = part.columns;
      state.board = boardOf(part);
      return state;
    }));
  }

  public finishPuzzle() {
    this.puzzleId = null;
    this.parts = new ArraySchema<PartState>();
  }

  public applySettings(settings: RoomSettings): void {
    Object.assign(this.settings, settings);
  }

  /** Seats a player, keyed by their connection's session (`player.id` is who they are in the room, the same across visits). */
  public addPlayer(sessionId: string, player: Player): void {
    this.players.set(sessionId, player);
  }

  public removePlayer(sessionId: string): void {
    this.players.delete(sessionId);
  }
}

/** A puzzle's fresh board (its revealed cells set). */
function boardOf(puzzle: PuzzleDefinition): ArraySchema<number> {
  const values = initialBoard(puzzle);
  const board = new ArraySchema<number>(values.length);
  values.forEach((value, i) => { board[i] = value ?? CellValue.UNFILLED; });
  return board;
}

